import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Button, Badge, ListGroup, Spinner, Alert } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const DoctorProfile = () => {
  const [doctorData, setDoctorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeNav, setActiveNav] = useState('Doctors');
  const navigate = useNavigate();

  // API endpoint configuration
  const API_ENDPOINT = 'https://api.example.com/doctors/MDF-D003'; // Replace with your actual API endpoint

  // Mock data for fallback
  const getMockData = useCallback(() => ({
    id: "MDF-D003",
    name: "Dr. Laurent Blake",
    specialty: "Child Psychologist",
    image: "https://cdn-icons-png.flaticon.com/512/3135/3135715.png",
    stats: {
      totalPatients: 520,
      recoveryRate: "87%",
      rating: "4.8 / 5",
      todayCounselling: 5,
      sessionsCompleted: 350,
      upcomingSessions: 15
    },
    description: "Dr. Laurent Blake is a highly experienced Child Psychologist who works with children experiencing behavioral challenges. She offers therapy through cognitive behavioral techniques.",
    education: [
      "PhD in Child Psychology",
      "MSc in Clinical Psychology"
    ],
    experience: [
      "10+ years in practice",
      "500+ children treated",
      "Specialized in anxiety, depression, and developmental issues"
    ],
    reviews: [
      {
        patient: "Emma Wilson",
        rating: 5,
        comment: "Dr. Blake was kind and helpful during therapy sessions. My child improved noticeably."
      },
      {
        patient: "Sophie Davis",
        rating: 5,
        comment: "Our family communication has improved. Highly recommend Dr. Blake for therapy."
      },
      {
        patient: "Liam Thompson",
        rating: 4,
        comment: "Sessions were effective and thoughtfully guided. Helped address behavioral issues."
      }
    ],
    schedule: [
      {
        time: "09:00 AM",
        patient: "Emma Wilson",
        type: "Completed",
        status: "Completed"
      },
      {
        time: "10:30 AM",
        patient: "Ethon James",
        type: "Individual Therapy",
        status: "Ongoing"
      },
      {
        time: "02:00 PM",
        patient: "Sophie Davis",
        type: "Family Counselling",
        status: "Pending"
      }
    ]
  }), []);

  const fetchDoctorData = useCallback(async () => {
    try {
      setLoading(true);
      // First try to fetch from API
      const response = await axios.get(API_ENDPOINT);
      setDoctorData(response.data);
    } catch (err) {
      console.error('API Error:', err);
      setError('Failed to fetch doctor data. Using demo data instead.');
      // Fallback to mock data if API fails
      setDoctorData(getMockData());
    } finally {
      setLoading(false);
    }
  }, [API_ENDPOINT, getMockData]);

  useEffect(() => {
    fetchDoctorData();
  }, [fetchDoctorData]);

  const handleNavClick = (navItem) => {
    setActiveNav(navItem);
    // Navigate to different pages in a real app
    navigate(`/${navItem.toLowerCase()}`);
  };

  const handleEditProfile = () => {
    console.log('Edit profile clicked');
    // In a real app, you would navigate to edit page or open a modal
    navigate('/doctors/edit/MDF-D003');
  };

  const renderStars = (rating) => {
    return (
      <span className="text-warning">
        {Array(5).fill().map((_, i) => (
          <span key={i}>{i < rating ? '★' : '☆'}</span>
        ))}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Completed': return 'success';
      case 'Ongoing': return 'info';
      case 'Pending': return 'warning';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error && !doctorData) {
    return (
      <div className="container py-4">
        <Alert variant="danger">{error}</Alert>
      </div>
    );
  }

  const navItems = [
    'Dashboard',
    'Doctors',
    'Patients',
    'Departments',
    'Medicines',
    'Invoices',
    'Emails'
  ];

  return (
    <div className="container py-4">
      <Row>
        {/* Sidebar */}
        <Col lg={3} className="mb-4">
          <Card className="h-100">
            <Card.Body>
              <h5 className="mb-4">Navigation</h5>
              <ListGroup variant="flush">
                {navItems.map((item) => (
                  <ListGroup.Item 
                    key={item}
                    action
                    active={activeNav === item}
                    onClick={() => handleNavClick(item)}
                    className="cursor-pointer"
                  >
                    {item}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>

        {/* Main Content */}
        <Col lg={9}>
          {doctorData && (
            <>
              <Card className="mb-4">
                <Card.Body>
                  <Row className="align-items-center">
                    <Col xs={12} md={3} className="text-center mb-3 mb-md-0">
                      <img
                        src={doctorData.image}
                        alt="Doctor"
                        className="img-fluid rounded-circle mb-3"
                        width="100"
                        height="100"
                      />
                      <h5>{doctorData.name}</h5>
                      <p className="text-muted mb-0">{doctorData.specialty}</p>
                      <p className="text-muted small">ID: {doctorData.id}</p>
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={handleEditProfile}
                      >
                        Edit Profile
                      </Button>
                    </Col>
                    <Col xs={12} md={9}>
                      <Row className="text-center g-3">
                        <Col xs={4} sm={4} md={4}>
                          <h6>{doctorData.stats.totalPatients}</h6>
                          <p className="mb-0">Total Patients</p>
                        </Col>
                        <Col xs={4} sm={4} md={4}>
                          <h6>{doctorData.stats.recoveryRate}</h6>
                          <p className="mb-0">Recovery Rate</p>
                        </Col>
                        <Col xs={4} sm={4} md={4}>
                          <h6>{doctorData.stats.rating}</h6>
                          <p className="mb-0">Review</p>
                        </Col>
                        <Col xs={4} sm={4} md={4}>
                          <h6>{doctorData.stats.todayCounselling}</h6>
                          <p className="mb-0">Today's Counselling</p>
                        </Col>
                        <Col xs={4} sm={4} md={4}>
                          <h6>{doctorData.stats.sessionsCompleted}</h6>
                          <p className="mb-0">Sessions Completed</p>
                        </Col>
                        <Col xs={4} sm={4} md={4}>
                          <h6>{doctorData.stats.upcomingSessions}</h6>
                          <p className="mb-0">Upcoming Sessions</p>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Row>
                <Col md={6} className="mb-4">
                  <Card className="h-100">
                    <Card.Body>
                      <h5>Description</h5>
                      <p>{doctorData.description}</p>
                      <h6>Education</h6>
                      <ul>
                        {doctorData.education.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                      <h6>Experience</h6>
                      <ul>
                        {doctorData.experience.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6} className="mb-4">
                  <Card className="h-100">
                    <Card.Body>
                      <h5>Patients' Reviews</h5>
                      {doctorData.reviews.map((review, index) => (
                        <div key={index} className="mb-3">
                          <strong>{review.patient}</strong> {renderStars(review.rating)}
                          <p className="mb-0">{review.comment}</p>
                        </div>
                      ))}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Card>
                <Card.Body>
                  <h5>Today's Counselling</h5>
                  <ListGroup variant="flush">
                    {doctorData.schedule.map((session, index) => (
                      <ListGroup.Item key={index}>
                        <Row className="align-items-center">
                          <Col xs={12} sm={3} className="mb-2 mb-sm-0">
                            <strong>{session.time}</strong>
                          </Col>
                          <Col xs={12} sm={6} className="mb-2 mb-sm-0">
                            {session.patient} - <em>{session.type}</em>
                          </Col>
                          <Col xs={12} sm={3} className="text-sm-end">
                            <Badge bg={getStatusBadge(session.status)}>{session.status}</Badge>
                          </Col>
                        </Row>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Card.Body>
              </Card>
            </>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default DoctorProfile;